import { createReducer } from "@reduxjs/toolkit";
import { isLocalStorageEntry, setLocalStorageItemKey } from "common/utilities/browser/localStorage/localStorage";
import { arrayLast, arrayUnique } from "common/utilities/core/array";
import { newItem, saveItem } from "core/redux/actions/localStorage/localStorageActions";
import type { AlignmentOptions } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import { LABEL_COMPONENT } from "sequenceAnalyser/global/settings.ts";
import {
  addIdsToSelection,
  changeExportToPNGStatus,
  clearSelection,
  createSearchActiveMatch,
  createSelection,
  removedSequences,
  removedSequencesInserted,
  removeIdsFromSelection,
  resetZoom,
  runAlignment,
  updateHasColorsAndFeaturesVisible,
  updateLabelCompositionSetting,
  updateScrollPosition,
  updateShowHoveredResidueTooltipSetting,
  updateShowSequenceNavigatorSetting,
  updateShowSimilarityTreeSetting,
  updateSidebarPixelWidth,
  updateVisualisationsSetting,
  zoomIn,
  zoomOut,
} from "sequenceAnalyser/redux/actions/sequenceAnalyserActions";
import type { SequenceSelectionWithSequenceData } from "sequenceAnalyser/utilities/selection/selection";

import type { SequenceAnalyserState } from "./sequenceAnalyserReducer.types";

export const LOCAL_STORAGE_SEQUENCES = "SEQUENCES";
setLocalStorageItemKey(LOCAL_STORAGE_SEQUENCES);

export const defaultRunAlignment: AlignmentOptions = {
  methodKey: "fast",
};

const initialState = (): SequenceAnalyserState => ({
  selection: {
    ids: [],
  },
  storedSequenceSelection: [],
  zoomLevel: 100,
  hasColorsAndFeaturesVisible: false,
  settings: {
    showHoveredResidueTooltip: true,
    visualisations: [],
    labelComposition: [
      LABEL_COMPONENT.GENOME,
      LABEL_COMPONENT.SEQUENCE_NAME,
      LABEL_COMPONENT.LOCATION,
      LABEL_COMPONENT.COMPLEMENT,
    ],
    showSequencesNavigator: false,
    showSimilarityTree: false,
  },
  sidebarPixelWidth: 200,
  removedSequences: [],
  ranAlignments: [defaultRunAlignment],
});

const sequenceAnalyserReducer = createReducer(initialState(), (builder) =>
  builder

    // Export
    .addCase(changeExportToPNGStatus, (state, action) => ({
      ...state,
      exportToPNGStatus: action.payload.exportToPNGStatus,
    }))
    // Alignments
    .addCase(runAlignment, (state, action) => ({
      ...state,
      ranAlignments: [...state.ranAlignments, action.payload.alignmentOptions],
    }))
    // Selection
    .addCase(createSelection, (state, action) => ({ ...state, selection: action.payload.selection }))
    .addCase(clearSelection, (state, action) => ({ ...state, selection: action.payload.selection }))
    .addCase(addIdsToSelection, (state, action) => ({
      ...state,
      selection: {
        positionStart: undefined,
        positionEnd: undefined,
        ids: arrayUnique([
          ...(state.selection.positionStart && state.selection.positionEnd ? [] : state.selection.ids),
          ...action.payload.ids,
        ]),
        lastId: arrayLast<number>(action.payload.ids),
      },
    }))
    .addCase(removeIdsFromSelection, (state, action) => ({
      ...state,
      selection: {
        positionStart: undefined,
        positionEnd: undefined,
        ids: state.selection.ids.filter((id) => !action.payload.ids.includes(id)),
        lastId: arrayLast<number>(action.payload.ids),
      },
    }))
    // Removed sequences
    .addCase(removedSequences, (state, action) => ({
      ...state,
      removedSequences: [...state.removedSequences, ...action.payload.sequences],
    }))
    .addCase(removedSequencesInserted, (state, action) => ({
      ...state,
      removedSequences: state.removedSequences.filter(
        (removedSequence) => !action.payload.sequences.map((s) => s.sequence.id).includes(removedSequence.sequence.id),
      ),
    }))
    // Zoom level
    .addCase(zoomIn, (state) => ({ ...state, zoomLevel: state.zoomLevel + 5 }))
    .addCase(zoomOut, (state) => ({ ...state, zoomLevel: state.zoomLevel - 5 }))
    .addCase(resetZoom, (state) => ({ ...state, zoomLevel: 100 }))
    // Search
    .addCase(createSearchActiveMatch, (state, action) => ({ ...state, searchActiveMatch: action.payload.match }))
    // Copy/insert sequences involving localstorage
    .addCase(newItem, (state, action) => {
      const { item } = action.payload;

      if (isLocalStorageEntry<SequenceSelectionWithSequenceData>(LOCAL_STORAGE_SEQUENCES, item)) {
        return { ...state, storedSequenceSelection: [...state.storedSequenceSelection, item.data] };
      }
      return { ...state };
    })
    .addCase(saveItem, (state, action) => {
      const { item } = action.payload;

      if (isLocalStorageEntry<SequenceSelectionWithSequenceData>(LOCAL_STORAGE_SEQUENCES, item)) {
        return { ...state, storedSequenceSelection: [...state.storedSequenceSelection, item.data] };
      }
      return { ...state };
    })
    // Settings
    .addCase(updateShowHoveredResidueTooltipSetting, (state, action) => ({
      ...state,
      settings: { ...state.settings, showHoveredResidueTooltip: action.payload.show },
    }))
    .addCase(updateVisualisationsSetting, (state, action) => ({
      ...state,
      settings: { ...state.settings, visualisations: action.payload.visualisations },
    }))
    .addCase(updateLabelCompositionSetting, (state, action) => {
      return { ...state, settings: { ...state.settings, labelComposition: action.payload.labelComposition } };
    })
    .addCase(updateShowSequenceNavigatorSetting, (state, action) => ({
      ...state,
      settings: { ...state.settings, showSequencesNavigator: action.payload.show },
    }))
    .addCase(updateShowSimilarityTreeSetting, (state, action) => ({
      ...state,
      settings: { ...state.settings, showSimilarityTree: action.payload.show },
    }))
    // UI
    .addCase(
      updateSidebarPixelWidth,
      (state, action): SequenceAnalyserState => ({ ...state, sidebarPixelWidth: action.payload.width }),
    )
    .addCase(updateHasColorsAndFeaturesVisible, (state, action) => ({
      ...state,
      hasColorsAndFeaturesVisible: action.payload.hasColorsAndFeaturesVisible,
    }))
    // Scroll position
    .addCase(updateScrollPosition, (state, action) => ({ ...state, scrollPosition: action.payload.scrollPosition })),
);

export default sequenceAnalyserReducer;
